import { Button } from "components/ui/button";
import { Sheet, SheetHeader, SheetTitle } from "components/ui/sheet";
import { SheetContent } from "../utils/agent-sheet-custom";
import { ChevronRight, Sparkles } from "lucide-react";
import {
  ConversationSession,
  ConversationSource,
  EnhancementQueryAnalysis,
  WorkflowTaskResult,
} from "../../../types/conversation.model";
import { getProviderDisplayName } from "../utils/aimodel-provider.utils";
import {
  GuardrailsSection,
  RetrievalSection,
  ToolsSection,
  WorkflowStepsSection,
} from "./agent-conversation-source-components";
import { useEffect, useRef, useState } from "react";
import { useAgentConversationStore } from "../utils/agent-conversation-store";
import { cn } from "lib/utils";
import { useMediaQuery } from "hooks/use-media-query";
import { useTheme } from "components/core/theme-provider";

type AgentConversationSourceTriggerProps = {
  onClick: () => void;
  isOpen?: boolean;
};

export const AgentConversationSourceTrigger = ({
  onClick,
  isOpen,
}: AgentConversationSourceTriggerProps) => {
  const { theme } = useTheme();
  
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        "flex items-center rounded-full py-1",
        isOpen && [
          "border",
          theme === "dark" ? "border-gray-500 bg-secondary" : "border-gray-300 bg-gray-100"
        ]
      )}
      aria-label="View conversation details"
      onClick={onClick}
    >
      <Sparkles className="h-3 w-3" />
      <span className="ml-1 text-xs">Details</span>
    </Button>
  );
};

type AgentConversationDetailsSheetProps = {
  session: ConversationSession | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AgentConversationDetailsSheet = ({
  session,
  isOpen,
  onOpenChange,
}: AgentConversationDetailsSheetProps) => {
  const { detailsWidth, setDetailsWidth } = useAgentConversationStore();
  const [isResizing, setIsResizing] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const contentRef = useRef<HTMLDivElement>(null);
  const currentWidthRef = useRef(detailsWidth);
  const { theme } = useTheme();

  useEffect(() => {
    if (!isResizing) {
      currentWidthRef.current = detailsWidth;
    }
  }, [detailsWidth, isResizing]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsResizing(true);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    setIsResizing(false);
    setDetailsWidth(currentWidthRef.current);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth > 300 && newWidth < 600) {
        currentWidthRef.current = newWidth;
        if (contentRef.current) {
          contentRef.current.style.width = `${newWidth}px`;
        }
      }
    }
  };

  if (!session) return null;

  const nodes = session.WorkflowTrace?.nodes ?? [];
  const guardrails = session.WorkflowTrace?.state?.results?.Violations || [];
  const toolExecutionResults = session.WorkflowTrace?.state?.results?.tool_execution_results || [];

  let tools =
    session.WorkflowTrace?.state?.results?.task_results?.filter((task) => !!task.tool_name) || [];

  if (tools.length === 0 && toolExecutionResults.length > 0) {
    const otherTools = toolExecutionResults.filter((t) => t.tool_name !== "search_knowledge_base");
    tools = otherTools.map(
      (t) =>
        ({
          task_id: 0,
          action: t.tool_name,
          success: t.success,
          started_at: t.started_at,
          completed_at: t.completed_at,
          tool_name: t.tool_name,
          tool_params: t.tool_params,
          tool_output: t.output,
        }) as WorkflowTaskResult,
    );
  }

  let retrievals =
    session.WorkflowTrace?.state?.results?.task_results
      ?.filter((task) => !!task.enhancement_query_analysis)
      ?.map((task) => task.enhancement_query_analysis!) || [];

  if (toolExecutionResults.length > 0) {
    const kbTools = toolExecutionResults.filter((t) => t.tool_name === "search_knowledge_base");
    const kbRetrievals: EnhancementQueryAnalysis[] = kbTools.map((t) => ({
      enhanced_queries: [t.tool_params?.query as string].filter(Boolean),
      score_of_adequate_context: 0,
    }));
    retrievals = [...retrievals, ...kbRetrievals];
  }

  const initialQuery = session.Query;
  let documents =
    session.WorkflowTrace?.state?.results?.task_results?.flatMap((task) => task.documents || []) ||
    [];

  if (documents.length === 0 && toolExecutionResults.length > 0) {
    const kbTools = toolExecutionResults.filter((t) => t.tool_name === "search_knowledge_base");
    const extraDocs = kbTools.flatMap((t) => {
      try {
        let parsed: any = t.output;
        if (typeof parsed === "string") {
          parsed = JSON.parse(parsed);
        }
        if (typeof parsed === "string") {
          parsed = JSON.parse(parsed);
        }

        if (parsed?.documents && Array.isArray(parsed.documents)) {
          return parsed.documents.map(
            (d: any) =>
              ({
                page_content: d.content,
                metadata: d.metadata || {},
              }) as ConversationSource,
          );
        }
        return [];
      } catch (e) {
        return [];
      }
    });
    documents = [...documents, ...extraDocs];
  }
  const metadata = session.Metadata;
  const modelProvider = session.WorkflowTrace?.nodes?.[0]?.tokens_used?.provider as string;
  const modelName = session.WorkflowTrace?.nodes?.[0]?.tokens_used?.model as string;

  return (
    <Sheet modal={false} open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        ref={contentRef}
        hideOverlay
        className="top-[60px] flex h-[calc(100vh-60px)] w-full !max-w-none flex-col p-4 shadow-xl transition-none data-[state=closed]:duration-300 data-[state=open]:duration-300 md:w-auto [&>button]:top-5"
        style={isDesktop ? { width: `${detailsWidth}px` } : undefined}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div
          className="group absolute left-0 top-0 z-50 hidden h-full w-6 -translate-x-1/2 cursor-ew-resize select-none items-center justify-center outline-none md:flex"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <div
            className={cn(
              "flex h-full w-3 items-center justify-center transition-all group-hover:bg-primary/20",
              theme === "dark" ? "bg-gray-700" : "bg-gray-200",
              isResizing && "bg-primary/30",
            )}
          >
            <div
              className={cn(
                "h-8 w-0.5 rounded-full transition-all group-hover:bg-primary",
                theme === "dark" ? "bg-gray-500" : "bg-gray-400",
                isResizing && "bg-primary",
              )}
            />
          </div>
        </div>
        <SheetHeader className="flex flex-row items-center gap-2">
          <Sparkles className="aspect-square w-4" />
          <SheetTitle className="!mt-0 text-left text-lg font-semibold">
            Generation Details
          </SheetTitle>
        </SheetHeader>

        <div className={cn(
          "flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-1.5",
          theme === "dark" 
            ? "[&::-webkit-scrollbar-thumb]:bg-gray-700" 
            : "[&::-webkit-scrollbar-thumb]:bg-gray-300",
          "[&::-webkit-scrollbar-thumb]:rounded-full"
        )}>
          {metadata && (
            <div className="mt-4 first:mt-0">
              <h3 className="text-md mb-2 font-semibold text-high-emphasis">Overview</h3>
              <div className="flex flex-col gap-1 text-sm text-medium-emphasis">
                <div className="flex items-center gap-1">
                  <span className="">Model:</span>
                  <span className="flex items-center gap-1 text-high-emphasis">
                    {getProviderDisplayName(modelProvider) || "Unknown"}{" "}
                    <ChevronRight className="h-3 w-3" /> {modelName || "Unknown"}
                  </span>
                </div>
                {metadata.query_type && (
                  <div className="flex gap-1">
                    <span className="">Query type:</span>
                    <span className="capitalize text-high-emphasis">{metadata.query_type}</span>
                  </div>
                )}
                {metadata.response_strategy && (
                  <div className="flex gap-1">
                    <span className="">Response Strategy:</span>
                    <span className="capitalize text-high-emphasis">
                      {metadata.response_strategy?.replace(/_/g, " ")}
                    </span>
                  </div>
                )}
                {metadata.retrievals_made !== undefined && (
                  <div className="flex gap-1">
                    <span className="">Retrievals Made:</span>
                    <span className="text-high-emphasis">{metadata.retrievals_made}</span>
                  </div>
                )}
                {metadata.tool_calls_made !== undefined && (
                  <div className="flex gap-1">
                    <span className="">Tool Calls Made:</span>
                    <span className="text-high-emphasis">{metadata.tool_calls_made}</span>
                  </div>
                )}
                <div className="flex gap-1">
                  <span className="">Processing Time:</span>
                  <span className="text-high-emphasis">
                    {(metadata.processing_time ?? metadata.duration ?? 0).toFixed(5)}s
                  </span>
                </div>
              </div>
            </div>
          )}

          <GuardrailsSection guardrails={guardrails} />

          <WorkflowStepsSection nodes={nodes} />

          <ToolsSection tools={tools} />

          {(retrievals.length > 0 ||
            (metadata?.retrievals_made !== undefined && metadata.retrievals_made > 0)) && (
            <RetrievalSection
              retrievals={retrievals}
              initialQuery={initialQuery}
              documents={documents}
            />
          )}

          {nodes.length === 0 &&
            retrievals.length === 0 &&
            guardrails.length === 0 &&
            tools.length === 0 &&
            !initialQuery &&
            documents.length === 0 && (
              <p className="mt-6 text-center text-sm text-medium-emphasis">No details available</p>
            )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
